import React, { useCallback, useEffect, useState } from 'react';
import { RawEditorSettings } from 'tinymce';
import { BeatLoader } from 'react-spinners';

import {
  ContentResourceType,
  getContentResources,
  IContentResource,
} from '../../../services/api';
import { buildEditorResourceHtml } from '../../../utils/contentResource';
import { useToast } from '../../../hooks/toast';
import { IPages } from '.';
import {
  LibraryActions,
  LibraryFilters,
  LibraryItem,
  LibraryList,
  LibraryPage,
} from './styles';

interface IProps {
  handlePage(page: IPages): void;
  propsEditor: RawEditorSettings;
  setIsOpen: () => void;
  providerId?: string;
}

const TYPE_LABELS: Record<ContentResourceType, string> = {
  video: 'Videoaula',
  podcast: 'Podcast',
  infographic: 'Infográfico',
  image: 'Imagem',
};

const ResourceLibrary: React.FC<IProps> = ({
  handlePage,
  propsEditor,
  setIsOpen,
  providerId,
}) => {
  const { addToast } = useToast();
  const [resources, setResources] = useState<IContentResource[]>([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<ContentResourceType | ''>('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await getContentResources({
          providerId,
          title: search || undefined,
          type: type || undefined,
          page,
          pageSize,
        });
        setResources(data.content);
        setCount(data.count);
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível carregar os recursos.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [addToast, page, providerId, search, type]);

  const handleSelect = useCallback(
    (resource: IContentResource) => {
      const html = buildEditorResourceHtml(resource);
      if (!html) {
        addToast({
          title: 'Ops ...',
          description: 'O recurso selecionado não possui uma versão válida.',
          type: 'error',
        });
        return;
      }

      propsEditor.insertContent(html);
      propsEditor.focus();
      setIsOpen();
    },
    [addToast, propsEditor, setIsOpen],
  );

  const pageCount = Math.max(1, Math.ceil(count / pageSize));

  return (
    <LibraryPage>
      <button type="button" onClick={() => handlePage('main')}>
        Voltar
      </button>
      <h2>Selecionar recurso existente</h2>
      <LibraryFilters>
        <input
          aria-label="Pesquisar recursos"
          value={search}
          onChange={event => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Pesquisar pelo título"
        />
        <select
          aria-label="Filtrar pelo tipo"
          value={type}
          onChange={event => {
            setType(event.target.value as ContentResourceType | '');
            setPage(1);
          }}
        >
          <option value="">Todos os tipos</option>
          <option value="video">Videoaulas</option>
          <option value="podcast">Podcasts</option>
          <option value="infographic">Infográficos</option>
          <option value="image">Imagens</option>
        </select>
      </LibraryFilters>

      <LibraryList>
        {loading && (
          <div className="library-message">
            <BeatLoader size={12} />
          </div>
        )}
        {!loading && resources.length === 0 && (
          <div className="library-message">Nenhum recurso encontrado.</div>
        )}
        {!loading &&
          resources.map(resource => (
            <LibraryItem key={resource.id}>
              <div>
                <strong>{resource.title}</strong>
                <span>
                  {TYPE_LABELS[resource.type]} ·{' '}
                  {resource.versions[0]?.status === 'publish'
                    ? 'Publicado'
                    : 'Rascunho'}
                </span>
              </div>
              <button type="button" onClick={() => handleSelect(resource)}>
                Inserir
              </button>
            </LibraryItem>
          ))}
      </LibraryList>

      <LibraryActions>
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage(currentPage => currentPage - 1)}
        >
          Anterior
        </button>
        <span>
          Página {page} de {pageCount}
        </span>
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => setPage(currentPage => currentPage + 1)}
        >
          Próxima
        </button>
      </LibraryActions>
    </LibraryPage>
  );
};

ResourceLibrary.defaultProps = {
  providerId: undefined,
};

export default ResourceLibrary;
